<template>
  <BaseButtonDropdown
    v-model="visible"
    v-intersection="intersectionConfig"
    aria-label="Toggle dropdown"
    class="mention-dropdown text-silver"
    close-on-click
    :color="buttonColor"
    flat
    :icon="dropDown.icon"
    :icon-size="dropDown.size"
    padding="xs"
    round
    @hide="onVisibilityChange(false)"
    @show="onVisibilityChange(true)"
  >
    <div
      class="column dropdown-list"
      @click.stop
    >
      <!--This action is always displayed on action sheet-->
      <span
        class="item"
        @click="openPushToTeamModal"
      >
        <QIcon
          name="ion-ios-megaphone"
          size="20px"
        />Push to Team
      </span>
      <!--Action only available for articles, paper articles and magazines-->
      <span
        v-if="canShareMention"
        class="item"
        @click="openShareMentionModal"
      >
        <QIcon
          name="ion-ios-redo"
          size="20px"
        />Email or Share
      </span>
      <!--Action only available for media items (caption or radio clip)-->
      <span
        v-if="canRequestMediaItem"
        class="item"
        @click="openMediaItemRequest()"
      >
        <QIcon
          name="ion-md-download"
          size="20px"
        />Request Media Item
      </span>
      <!--Action only available for transcript request items (caption or radio clip)-->
      <span
        v-if="canRequestTranscriptItem"
        class="item"
        @click="openTranscriptRequest"
      >
        <QIcon
          name="ion-md-list-box"
          size="20px"
        />Request Transcript Item
      </span>
      <!--Action only available for articles-->
      <span
        v-if="canSeeFullText"
        class="item"
        @click="openSeeFullTextModal"
      >
        <QIcon
          name="ion-ios-paper"
          size="20px"
        />See Full Text
      </span>
      <!--Action available for all mention types except tweets-->
      <span
        v-if="canAddToOrganisationReport"
        class="item"
        @click="openAddToOrganisationReportModal"
      >
        <QIcon
          name="ion-md-mail"
          size="20px"
        />Add to Curated Email Report Folder
      </span>

      <!--Action only available if we have a valid stream-->
      <span
        v-if="canExcludeMention"
        class="item"
        @click="openExcludeMentionModal"
      >
        <QIcon
          name="ion-eye-off"
          size="20px"
        />
        Remove from Mention Stream
        <StreemTooltip>
          If the Item is incorrect or irrelevant, this will prevent it from
          appearing in this Stream or any Report built from this Stream.
          <br />
          Can be reviewed and undone in the Filters.
        </StreemTooltip>
      </span>
      <!--Action only available for a mention which is in a folder currently viewed-->
      <span
        v-if="$route.path === '/bookmarks'"
        class="item"
        @click="confirmRemoveFromBookmarkStream"
      >
        <QIcon
          name="ion-pricetag"
          size="20px"
        />Remove from Folder
      </span>
      <!--Action only available if we have a valid stream-->
      <span
        v-if="canShowDebugInfo"
        class="item"
        @click="openDebugMentionModal"
      >
        <QIcon
          name="ion-hammer"
          size="20px"
        />
        Show debug info
      </span>
    </div>
  </BaseButtonDropdown>
</template>

<script>
import { BaseButtonDropdown } from "shared/components/base";
import { mentionStreamTypes } from "shared/constants";
import MediaRequestService from "shared/services/api/MediaRequestService";
import TranscriptRequestService from "shared/services/api/TranscriptRequestService";
import { useBookmarkStreamsStore } from "shared/stores/bookmarkStreams";

export default {
  name: "ActionSheet",
  components: {
    BaseButtonDropdown,
  },
  props: {
    origin: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    mention: {
      type: Object,
      required: true,
    },
    stream: {
      type: Object,
      default: () => ({}),
    },
    dropDown: {
      type: Object,
      required: false,
      default: () => ({
        icon: "more_horiz",
        size: "sm",
      }),
    },
    buttonColor: {
      type: String,
      required: false,
      default: "inherit",
    },
    isFrontPage: {
      type: Boolean,
    },
  },
  emits: ["mention-removed"],
  setup() {
    const bookmarkStreamsStore = useBookmarkStreamsStore();

    const { removeFromBookmarkStream } = bookmarkStreamsStore;

    return {
      removeFromBookmarkStream,
    };
  },
  data() {
    return {
      visible: false,
      duplicateMediaItem: false,
      duplicateTranscriptRequest: false,
    };
  },
  computed: {
    canShareMention() {
      return ["article", "paper_article", "magazine_article"].includes(
        this.mention.type
      );
    },
    canSeeFullText() {
      return (
        !this.isFrontPage &&
        this.mention.type === "article" &&
        this.$features.has("full_article_body")
      );
    },
    canRequestMediaItem() {
      return (
        ["caption", "tv_super", "radio_clip"].includes(this.mention.type) &&
        this.$permissions.has("can_request_media_items")
      );
    },
    canRequestTranscriptItem() {
      return (
        ["caption", "tv_super", "radio_clip"].includes(this.mention.type) &&
        this.$permissions.has("can_request_transcripts")
      );
    },
    canAddToOrganisationReport() {
      return this.$permissions.has(
        "can_add_any_mention_to_organisation_reports"
      );
    },
    canShowDebugInfo() {
      return this.$permissions.has("has_advanced_access");
    },
    canExcludeMention() {
      return (
        this.stream &&
        this.stream.id &&
        mentionStreamTypes.includes(this.stream.type) &&
        this.$permissions.has("can_exclude_mentions") &&
        this.origin.origin_type !== "ReportingStream"
      );
    },
    intersectionConfig() {
      return this.visible ? { handler: this.onIntersection } : {};
    },
  },
  methods: {
    onIntersection({ isIntersecting }) {
      if (!isIntersecting && this.visible) {
        this.closeDropdown();
      }
    },
    onVisibilityChange(visible) {
      const { mention, stream } = this;
      const action = visible ? "Opened" : "Closed";

      this.$track(`${action} Action Sheet`, {
        id: mention.id,
        type: mention.type,
        streamId: stream.id,
      });

      this.visible = visible;
    },
    closeDropdown() {
      this.visible = false;
    },
    // The method below is only used from the bookmark view because we don't alter
    // the mentions in the other workflows and the bookmarkId is not present in the
    // individual mention
    async confirmRemoveFromBookmarkStream() {
      const { stream: bookmarkStream, mention } = this;

      this.closeDropdown();

      await this.removeFromBookmarkStream({
        bookmarkStream,
        mention,
      });

      this.$emit("mention-removed", this.mention);
    },
    async openMediaItemRequest() {
      this.closeDropdown();

      try {
        // eslint-disable-next-line prefer-destructuring
        this.duplicateMediaItem = (
          await MediaRequestService.findDuplicates(this.mention, {
            seconds: 300,
          })
        )[0];
      } catch {
        // don't do anything, just skip to the new request modal
      } finally {
        if (this.duplicateMediaItem) {
          this.openDuplicateMediaItemModal();
        } else {
          this.openRequestMediaItemModal();
        }
      }
    },
    async openTranscriptRequest() {
      this.closeDropdown();

      try {
        // eslint-disable-next-line prefer-destructuring
        this.duplicateTranscriptRequest = (
          await TranscriptRequestService.findDuplicates(this.mention, {
            seconds: 300,
          })
        )[0];
      } catch {
        // don't do anything, just skip to the new request modal
      } finally {
        if (this.duplicateTranscriptRequest) {
          this.openDuplicateTranscriptRequestModal();
        } else {
          this.openRequestTranscriptItemModal();
        }
      }
    },
    openShareMentionModal() {
      this.$modals.open("ShareMentionModal", {
        props: {
          mention: this.mention,
        },
      });
    },
    openPushToTeamModal() {
      this.$modals.open("PushToTeamModal", {
        props: {
          mention: this.mention,
        },
      });
    },
    openRequestMediaItemModal() {
      this.$modals.open("RequestMediaItemModal", {
        props: {
          mention: this.mention,
          stream: this.stream,
          origin: this.origin,
        },
      });
    },
    openDuplicateMediaItemModal() {
      this.$modals.open("DuplicateMediaItemTranscriptRequestModal", {
        props: {
          type: "media_item",
          shouldShowInstantEditButton: false,
        },
      });
    },
    openDuplicateTranscriptRequestModal() {
      this.$modals.open("DuplicateMediaItemTranscriptRequestModal", {
        props: {
          type: "transcript_request",
          shouldShowInstantEditButton: false,
        },
      });
    },
    openRequestTranscriptItemModal() {
      this.$modals.open("RequestTranscriptItemModal", {
        props: {
          mention: this.mention,
        },
      });
    },
    openSeeFullTextModal() {
      this.$modals.open("SeeFullTextModal", {
        props: {
          mention: this.mention,
        },
      });
    },
    openAddToOrganisationReportModal() {
      this.$modals.open("AddToOrganisationReportModal", {
        props: {
          mention: this.mention,
        },
      });
    },
    openExcludeMentionModal() {
      this.$modals.open("ExcludeMentionModal", {
        props: {
          mention: this.mention,
          stream: this.stream,
        },
      });
    },
    openDebugMentionModal() {
      this.$modals.open("DebugMentionModal", {
        props: {
          mention: this.mention,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.dropdown-list {
  max-height: 400px;
  overflow-y: scroll;
  flex-wrap: nowrap;
}
</style>
